import styles from "./TermInfoRollup.module.scss";
import { clsx } from "clsx";
import { FormattedMessage, useIntl } from "react-intl";
import BaseLinkOrButton from "../../../../common/core-ui/BaseLinkOrButton.tsx";
import useTermStoreMetrics from "./useTermStoreMetrics.tsx";
import useProgramMetrics from "./useProgramMetrics.tsx";
import useVirtualStore from "../../common/virtual-store/useVirtualStore.tsx";
import useTermOptDeadline from "./useTermOptDeadline.tsx";
import { useProgramDetails } from "./coursesLoader.ts";
import PillTag from "../common/PillTag.tsx";
import { useCallback, useState } from "react";
import TermInfoRollupDetailsDialog, {
  DialogType,
} from "./TermInfoRollupDetailsDialog.tsx";

function ProgramBlock() {
  const {
    scope,
    userEffectiveProgram,
    ea: { name: eaProgramName, allowsOptOut },
    name: programName,
  } = useProgramDetails();
  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

  const {
    requiredIncludedCount,
    hasOptableItems,
    totalOptedOutItems,
    totalOptableItems,
  } = useProgramMetrics();
  const { requiredToPurchaseCount } = useTermStoreMetrics();

  const isSomeVersionOfEA =
    scope === "ea_only" ||
    (scope === "flexible" && userEffectiveProgram === "equitable_access");

  const getProgramBlockData = useCallback(() => {
    const data: {
      name: string;
      optInCount?: number;
      optOutCount?: number;
      dialogType: Exclude<DialogType, "Purchase">;
      title: string;
    } = {
      name: isSomeVersionOfEA ? eaProgramName : programName,
      dialogType: "IaOptedIn",
      title: "",
    };

    const optedInItems = totalOptableItems - totalOptedOutItems;
    data.optInCount = optedInItems || undefined;
    data.optOutCount = totalOptedOutItems || undefined;

    if (isSomeVersionOfEA) {
      data.title = eaProgramName;
      const optedOutOfEA =
        scope === "ea_only" && userEffectiveProgram === "no_program";
      data.dialogType = optedOutOfEA ? "EaOptedOut" : "EaOptedIn";
    } else {
      data.title = programName;
      data.dialogType =
        optedInItems > 0 && totalOptedOutItems > 0
          ? "IaMixed"
          : optedInItems > 0
          ? "IaOptedIn"
          : "IaOptedOut";
    }
    return data;
  }, [
    eaProgramName,
    isSomeVersionOfEA,
    programName,
    scope,
    totalOptableItems,
    totalOptedOutItems,
    userEffectiveProgram,
  ]);

  if (scope === "ea_only" && !allowsOptOut) {
    return null;
  }

  if (userEffectiveProgram === "inclusive_access" && !hasOptableItems) {
    return null;
  }

  if (
    scope === "ea_only" &&
    !requiredIncludedCount &&
    !requiredToPurchaseCount
  ) {
    return null;
  }

  // null state if both 0
  // if included count 0 show program to emphasize could be part of program
  if (
    !requiredIncludedCount &&
    requiredToPurchaseCount &&
    userEffectiveProgram !== "inclusive_access"
  ) {
    return null;
  }

  const { name, optInCount, optOutCount, dialogType, title } =
    getProgramBlockData();
  return (
    <>
      <div className={styles.block} data-testid="program-rollup">
        <div className={styles.mainLine}>
          <div className={styles.caption}>{name}</div>
        </div>
        <div className={styles.secondLine}>
          {optInCount && <PillTag type="OptedIn" count={optInCount} />}
          {optOutCount && <PillTag type="OptedOut" count={optOutCount} />}
          <BaseLinkOrButton
            className={clsx("vst-link vst-link-size-small")}
            onClick={() => setIsDetailsDialogOpen(true)}
          >
            <FormattedMessage defaultMessage={"Details"} />
          </BaseLinkOrButton>
        </div>
      </div>
      {isDetailsDialogOpen && (
        <TermInfoRollupDetailsDialog
          type={dialogType}
          title={title}
          onRequestClose={() => setIsDetailsDialogOpen(false)}
        />
      )}
    </>
  );
}

function PurchaseBlock() {
  const intl = useIntl();
  const { forPurchaseCount } = useTermStoreMetrics();

  const [isDetailsDialogOpen, setIsDetailsDialogOpen] = useState(false);

  if (!forPurchaseCount) {
    return null;
  }

  return (
    <>
      <div className={styles.block} data-testid="purchase-rollup">
        <div className={styles.mainLine}>
          <div className={styles.caption}>
            <FormattedMessage defaultMessage={"For Purchase"} />
          </div>
        </div>
        <div className={styles.secondLine}>
          <FormattedMessage
            tagName="span"
            defaultMessage={"{requiredToPurchase} to Purchase"}
            values={{
              requiredToPurchase: forPurchaseCount,
            }}
          />
          <BaseLinkOrButton
            className={clsx("vst-link vst-link-size-small")}
            onClick={() => setIsDetailsDialogOpen(true)}
          >
            <FormattedMessage defaultMessage={"Details"} />
          </BaseLinkOrButton>
        </div>
      </div>
      {isDetailsDialogOpen && (
        <TermInfoRollupDetailsDialog
          type="Purchase"
          title={intl.formatMessage({ defaultMessage: "For Purchase" })}
          onRequestClose={() => setIsDetailsDialogOpen(false)}
        />
      )}
    </>
  );
}

export default function TermInfoRollup() {
  const { isVirtualStore } = useVirtualStore();
  const { optWindowOpen } = useTermOptDeadline();

  if (!isVirtualStore || !optWindowOpen) {
    return null;
  }
  return (
    <div className={styles.container}>
      <ProgramBlock />
      <PurchaseBlock />
    </div>
  );
}
